import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiStateCheckbox } from "primereact/multistatecheckbox";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLogUIEventCallback } from "../../../amplitude";
import * as client from "../../../client";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { ROUTES } from "../../../routes";
import { displayNameForUser } from "../../../util";
import {
  attachmentsUiTypeMap,
  sortAttachments,
} from "../attachments/AttachmentCard";
import { SafeReportResponse } from "../Conversion";
import { useLocalUser, useUpdateReport, useUserById } from "../data";
import { formatAbbreviatedMoney } from "../Money";
import styles from "./SummaryCard.module.css";
import {
  DisplayedType,
  jobResult,
  latestJobByType,
} from "../dashboard/IntegrationJobsCell";
import { useReportJobs } from "../rater/useIntegrationJobs";
import {
  lossRunInfoToLossRunYears,
  lossRunSummaryStats,
} from "../loss-run/util";

// ---------------------------------------------------------------------------
// Question registry – single source of truth for the frontend.
// To add/remove questions, just edit this array. The backend stores only
// user responses keyed by question_id, so no migration is needed.
// ---------------------------------------------------------------------------

type ReviewQuestion = {
  questionId: string;
  section: string;
  questionText: string;
  /** Path suffix for the "open in tab" link, if any */
  tabPath?: string;
  /** If true, render custom content below the question via AdditionalContent */
  hasAdditionalContent?: boolean;
  /**
   * When set, the checkbox is driven automatically by the latest job status
   * for these integration types. Checked = at least one type has a successful
   * latest run. The checkbox is not user-clickable when this is present.
   */
  autoCheckIntegrationTypes?: DisplayedType[];
};

const REVIEW_QUESTIONS: ReviewQuestion[] = [
  {
    questionId: "attachments",
    section: "Engineering Review",
    questionText: "Do the file classifications look accurate?",
    tabPath: "attachments",
    hasAdditionalContent: true,
  },
  {
    questionId: "tiv",
    section: "Engineering Review",
    questionText: "Does the total TIV look accurate?",
    tabPath: "sov",
    hasAdditionalContent: true,
  },
  {
    questionId: "policy-form",
    section: "Underwriting Steps",
    questionText: "Has the Policy Form been validated?",
    tabPath: "general-input",
  },
  {
    questionId: "sov",
    section: "Underwriting Steps",
    questionText:
      "Has the SOV data been validated? Pay special attention to # of Buildings, Occupancy, Construction, and Sprinker.",
    tabPath: "sov",
  },
  {
    questionId: "limits",
    section: "Underwriting Steps",
    questionText: "Do the extracted sublimits and peril limits look accurate?",
    tabPath: "limits",
  },
  {
    questionId: "deductibles",
    section: "Underwriting Steps",
    questionText: "Do the extracted deductibles look accurate?",
    tabPath: "deductibles",
  },
  {
    questionId: "modeling",
    questionText: "Have any CAT modeling runs completed?",
    section: "Underwriting Steps",
    tabPath: "cat",
    autoCheckIntegrationTypes: ["ICM", "RMS"],
  },
  {
    questionId: "rater",
    questionText: "Have any raters been generated?",
    section: "Underwriting Steps",
    tabPath: "rater",
    autoCheckIntegrationTypes: ["RATER"],
  },
  {
    questionId: "loss-run",
    section: "Underwriting Steps",
    questionText: "Do the extracted loss runs look accurate?",
    tabPath: "loss-run",
    hasAdditionalContent: true,
  },
  {
    questionId: "policy-fly",
    questionText: "Have any Policy Fly applications been generated?",
    section: "Underwriting Steps",
    autoCheckIntegrationTypes: ["POLICY_FLY"],
  },
];

// Group questions by section, preserving array order.
const questionsBySection = (): {
  section: string;
  questions: ReviewQuestion[];
}[] => {
  const seen = new Map<string, ReviewQuestion[]>();
  for (const q of REVIEW_QUESTIONS) {
    if (!seen.has(q.section)) seen.set(q.section, []);
    seen.get(q.section)!.push(q);
  }
  return Array.from(seen.entries()).map(([section, questions]) => ({
    section,
    questions,
  }));
};

// ---------------------------------------------------------------------------
// Additional content renderers per question
// ---------------------------------------------------------------------------

const AdditionalContent: React.FC<{
  questionId: string;
  specialtyPropertyInfo: SafeReportResponse;
  rawSpecialtyPropertyInfo: client.ReportResponse;
}> = ({ questionId, specialtyPropertyInfo }) => {
  if (questionId === "attachments") {
    return <AttachmentsSummary specialtyPropertyInfo={specialtyPropertyInfo} />;
  }

  if (questionId === "tiv") {
    return <TivSummary specialtyPropertyInfo={specialtyPropertyInfo} />;
  }

  if (questionId === "loss-run") {
    return <LossRunSummary specialtyPropertyInfo={specialtyPropertyInfo} />;
  }

  return null;
};

const AttachmentsSummary: React.FC<{
  specialtyPropertyInfo: SafeReportResponse;
}> = ({ specialtyPropertyInfo }) => {
  const attachments =
    specialtyPropertyInfo.report_json.attachment_info?.attachments ?? [];
  sortAttachments(attachments);

  if (attachments.length === 0) {
    return <span className={styles.policyExcerpt}>No attachments found.</span>;
  }

  return (
    <div className={styles.excerptContainer}>
      <div className={styles.verticalDivider} />
      <div className={styles.policyExcerpt}>
        <span style={{ fontWeight: "bold" }}>Attachments</span>
        <span>
          {attachments
            .map(
              (a) =>
                `${
                  attachmentsUiTypeMap[a.manual_mapping ?? a.original_mapping]
                } — ${a.name}`
            )
            .join("\n")}
        </span>
      </div>
    </div>
  );
};

const TivSummary: React.FC<{
  specialtyPropertyInfo: SafeReportResponse;
}> = ({ specialtyPropertyInfo }) => {
  const totalTIV = useMemo(
    () =>
      specialtyPropertyInfo.report_json.sovs
        .filter((sov) => sov.is_enabled)
        .flatMap((sov) => sov.addresses)
        .reduce((sum, addr) => sum + addr.tiv, 0),
    [specialtyPropertyInfo]
  );

  return (
    <div className={styles.excerptContainer}>
      <div className={styles.verticalDivider} />
      <div className={styles.policyExcerpt}>
        <span style={{ fontWeight: "bold" }}>Total TIV</span>
        <span>{formatAbbreviatedMoney(totalTIV)}</span>
      </div>
    </div>
  );
};

const LossRunSummary: React.FC<{
  specialtyPropertyInfo: SafeReportResponse;
}> = ({ specialtyPropertyInfo }) => {
  const lossRunInfo = specialtyPropertyInfo.report_json.loss_run;
  const lossRunYears = lossRunInfoToLossRunYears(lossRunInfo);
  const stats = lossRunSummaryStats(lossRunYears);

  if (stats.numYears === 0) {
    return (
      <span className={styles.policyExcerpt}>No loss run data found.</span>
    );
  }

  return (
    <div>
      <Message
        className={styles.customMessage}
        severity={stats.totalOpenClaims > 1 ? "warn" : "info"}
        text={`Open Claims: ${stats.totalOpenClaims}`}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Top-level card
// ---------------------------------------------------------------------------

type SummaryCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
  rawSpecialtyPropertyInfo: client.ReportResponse;
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  specialtyPropertyInfo,
  rawSpecialtyPropertyInfo,
}) => {
  return (
    <Card title="Review">
      <HumanReviewSection
        specialtyPropertyInfo={specialtyPropertyInfo}
        rawSpecialtyPropertyInfo={rawSpecialtyPropertyInfo}
      />
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Review section – iterates the question registry and merges with responses
// ---------------------------------------------------------------------------

const HumanReviewSection: React.FC<{
  specialtyPropertyInfo: SafeReportResponse;
  rawSpecialtyPropertyInfo: client.ReportResponse;
}> = ({ specialtyPropertyInfo, rawSpecialtyPropertyInfo }) => {
  const responses = specialtyPropertyInfo.review_info?.responses ?? {};
  const sections = questionsBySection();

  return (
    <div>
      {sections.map(({ section, questions }, idx) => (
        <div key={section}>
          <Card title={section} className={styles.perilCard}>
            <div className={styles.humanReviewItemsContainer}>
              {questions.map((q) => (
                <HumanReviewRow
                  key={q.questionId}
                  definition={q}
                  response={responses[q.questionId] ?? null}
                  specialtyPropertyInfo={specialtyPropertyInfo}
                  rawSpecialtyPropertyInfo={rawSpecialtyPropertyInfo}
                />
              ))}
            </div>
          </Card>
          {idx < sections.length - 1 && <Divider />}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Auto-check hook – derives checkbox state from the latest job status
// ---------------------------------------------------------------------------

function useAutoCheckStatus(
  reportId: string,
  integrationTypes: DisplayedType[] | undefined
): { isAuto: false } | { isAuto: true; status: string } {
  const { jobs } = useReportJobs(reportId);

  return useMemo(() => {
    if (!integrationTypes || integrationTypes.length === 0) {
      return { isAuto: false };
    }

    const latest = latestJobByType(jobs);
    const hasSuccess = integrationTypes.some(
      (type) => jobResult(latest[type]).status === "success"
    );

    return { isAuto: true, status: hasSuccess ? "success" : "none" };
  }, [integrationTypes, jobs]);
}

// ---------------------------------------------------------------------------
// Single review row
// ---------------------------------------------------------------------------

const DEFAULT_STATUS = "none";

const HumanReviewRow: React.FC<{
  definition: ReviewQuestion;
  response: client.ReviewQuestionResponse | null;
  specialtyPropertyInfo: SafeReportResponse;
  rawSpecialtyPropertyInfo: client.ReportResponse;
}> = ({
  definition,
  response,
  specialtyPropertyInfo,
  rawSpecialtyPropertyInfo,
}) => {
  const autoCheck = useAutoCheckStatus(
    specialtyPropertyInfo.report_id,
    definition.autoCheckIntegrationTypes
  );
  const status = autoCheck.isAuto
    ? autoCheck.status
    : (response?.status ?? DEFAULT_STATUS);
  const savedWarning = response?.warning_message ?? null;

  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);
  const localUser = useLocalUser();
  const author = useUserById(response?.last_updated_user_id);
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(savedWarning);

  useEffect(() => {
    setNoteText(savedWarning);
  }, [savedWarning]);

  const canExpand =
    (savedWarning && savedWarning !== "") ||
    status !== DEFAULT_STATUS ||
    definition.hasAdditionalContent;
  const [isExpanded, setIsExpanded] = useState(canExpand);

  const saveResponse = async (
    patch: Partial<client.ReviewQuestionResponse>
  ) => {
    const newResponse: client.ReviewQuestionResponse = {
      question_id: definition.questionId,
      status: response?.status ?? DEFAULT_STATUS,
      warning_message: response?.warning_message ?? null,
      last_updated_ts: new Date().toISOString(),
      last_updated_user_id: localUser?.id ?? null,
      ...patch,
    };
    await updateReport.mutateAsync({ reviewQuestionResponse: newResponse });
  };

  return (
    <div className={styles.humanReviewItem}>
      <div className={styles.humanReviewItemRow}>
        <HumanReviewCheckbox
          status={status}
          disabled={isSaving || autoCheck.isAuto}
          onToggle={async (newStatus) => {
            setIsSaving(true);
            await saveResponse({ status: newStatus });
            setIsSaving(false);
          }}
        />
        <span
          style={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {definition.questionText}
        </span>
        <div className={styles.buttonGroup}>
          {canExpand && (
            <Button
              icon={isExpanded ? "pi pi-angle-down" : "pi pi-angle-up"}
              text
              size="small"
              className={styles.smallButton}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
          <Button
            icon="pi pi-pencil"
            text
            size="small"
            className={styles.smallButton}
            onClick={() => setIsEditingNote(!isEditingNote)}
          />
          {definition.tabPath && (
            <Button
              icon="pi pi-external-link"
              size="small"
              text
              className={styles.smallButton}
              onClick={() =>
                navigate(
                  ROUTES.DASHBOARD_REPORT(
                    specialtyPropertyInfo.report_id,
                    definition.tabPath!
                  ),
                  { replace: true }
                )
              }
            />
          )}
        </div>
      </div>

      {((canExpand && isExpanded) || isEditingNote) && (
        <div
          className={styles.warningVerticalContainer}
          style={{ marginLeft: "30px", marginBottom: "8px" }}
        >
          {definition.hasAdditionalContent && (
            <AdditionalContent
              questionId={definition.questionId}
              specialtyPropertyInfo={specialtyPropertyInfo}
              rawSpecialtyPropertyInfo={rawSpecialtyPropertyInfo}
            />
          )}

          {(savedWarning || status === "success" || isEditingNote) && (
            <div className={styles.verticalContainerSmallGap}>
              <div>
                <Message
                  className={styles.customMessage}
                  severity={savedWarning || isEditingNote ? "info" : "success"}
                  content={
                    !isEditingNote ? (
                      <div className={styles.warningHorizontalContainer}>
                        <span>
                          {savedWarning ||
                            (autoCheck.isAuto
                              ? "Last run completed successfully."
                              : "Looks good to me.")}
                        </span>
                      </div>
                    ) : (
                      <div className={styles.warningVerticalContainer}>
                        <InputTextarea
                          disabled={isSaving}
                          value={noteText ?? ""}
                          onChange={(e) => setNoteText(e.target.value)}
                          rows={2}
                          cols={40}
                          placeholder="Add a note"
                        />
                        <div
                          className={styles.warningHorizontalContainer}
                          style={{ justifyContent: "flex-end" }}
                        >
                          <Button
                            size="small"
                            label="Cancel"
                            disabled={isSaving}
                            severity="secondary"
                            raised
                            style={{ padding: "6px 8px" }}
                            onClick={() => {
                              setNoteText(savedWarning);
                              setIsEditingNote(false);
                            }}
                          />
                          <Button
                            size="small"
                            label="Save"
                            loading={isSaving}
                            raised
                            style={{ padding: "6px 8px" }}
                            disabled={noteText === null}
                            onClick={async () => {
                              setIsSaving(true);
                              await saveResponse({ warning_message: noteText });
                              setIsEditingNote(false);
                              setIsExpanded(true);
                              setIsSaving(false);
                            }}
                          />
                        </div>
                      </div>
                    )
                  }
                />
              </div>
              {!isEditingNote && author && response?.last_updated_ts && (
                <AvatarWithTimestamp
                  ts={response.last_updated_ts}
                  author={author}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Checkbox
// ---------------------------------------------------------------------------

const HumanReviewCheckbox: React.FC<{
  status: string;
  disabled: boolean;
  onToggle: (newStatus: string) => Promise<void>;
}> = ({ status, disabled, onToggle }) => {
  const [isSaving, setIsSaving] = useState(false);
  const logUIEvent = useLogUIEventCallback();
  const options = [{ value: "success", icon: "pi pi-check" }];

  if (isSaving) {
    return (
      <MultiStateCheckbox
        value="spin"
        options={[{ value: "spin", icon: "pi pi-spin pi-spinner" }]}
        optionValue="value"
      />
    );
  }

  return (
    <MultiStateCheckbox
      disabled={disabled}
      value={status}
      onChange={async (e) => {
        const newStatus = e.value === "success" ? "success" : "none";
        logUIEvent("asgard_click_human_review_checkbox", {
          check_status: newStatus,
        });
        setIsSaving(true);
        await onToggle(newStatus);
        setIsSaving(false);
      }}
      options={options}
      optionValue="value"
    />
  );
};

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

const AvatarWithTimestamp: React.FC<{
  author: client.User;
  ts: string;
}> = ({ author, ts }) => {
  const date = new Date(ts);
  const formatter = new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <div className={styles.warningHorizontalContainer}>
      <PantheonAvatarWithoutMenu label={displayNameForUser(author)} />
      <span
        style={{
          fontSize: "12px",
          color: "var(--text-color-secondary)",
          flexShrink: "0",
        }}
      >
        {formatter.format(date)}
      </span>
    </div>
  );
};
